<template>
    <div class="specialist-list-view">
        <h1 class="app-h1">
            Список специалистов
        </h1>

        <div class="specialist-list-view__toolbar">
            <el-input
                v-model.trim="search"
                class="specialist-list-view__search"
                clearable
                placeholder="Поиск"
            />

            <el-button
                type="primary"
                @click="_openAddForm"
            >
                Добавить
            </el-button>

            <el-button
                type="danger"
                :loading="exportPasswordsLoading"
                @click="_exportPasswords"
            >
                Сгенерировать пароли
            </el-button>
        </div>

        <specialist-list
            ref="list"
            :search="search"
            @edit="_onSpecialistEdit"
            @password-reset="_onSpecialistPasswordReset"
        />

        <br>
        <br>

        <specialist-form-popup
            ref="form"
            @added="_onSpecialistAdded"
            @updated="_onSpecialistUpdated"
        />

        <el-dialog
            title="Новый пароль специалиста"
            :visible.sync="passwordResetDialogVisible"
            :close-on-click-modal="false"
            width="520px"
        >
            <div class="specialist-list-view__password-card-header">
                <div>{{ passwordResetDialogTitle }}</div>
                <el-button
                    title="Скопировать"
                    @click="_copyPasswordCard"
                >
                    <i class="far fa-copy" />
                </el-button>
            </div>

            <el-input
                ref="passwordCard"
                :value="passwordResetCard"
                type="textarea"
                :rows="6"
                readonly
            />

            <div class="specialist-list-view__password-card-footer">
                <el-button
                    type="primary"
                    @click="passwordResetDialogVisible = false"
                >
                    Закрыть
                </el-button>
            </div>
        </el-dialog>
    </div>
</template>

<script>
import _ from 'lodash';
import SpecialistList from '../components/SpecialistList';
import breadcrumbs from '@/mixins/breadcrumbs';
import { Specialist } from '@/api';
import { ROUTES } from '@/enums';
import SpecialistFormPopup from '@/modules/specialist/components/SpecialistFormPopup';

export default {
    name: 'SpecialistListView',

    components: {
        SpecialistFormPopup,
        SpecialistList
    },

    mixins: [
        breadcrumbs
    ],

    data() {
        return {
            search: '',
            exportPasswordsLoading: false,
            passwordResetDialogVisible: false,
            passwordResetResult: null
        };
    },

    computed: {
        $breadcrumbs() {
            return [
                {
                    title: 'Услуги',
                    route: null
                },
                {
                    title: 'Специалисты',
                    route: ROUTES.SPECIALIST.LIST
                }
            ];
        },

        passwordResetDialogTitle() {
            return _.get(this.passwordResetResult, 'name', '');
        },

        passwordResetCard() {
            return _.get(this.passwordResetResult, 'card', '');
        }
    },

    methods: {
        _onSpecialistEdit() {
            this.$refs.form.open();
        },

        _openAddForm() {
            this.$refs.form.open();
        },

        /**
         * @param {SpecialistResource} specialist
         * @private
         */
        _onSpecialistAdded({ specialist }) {
            this.$message.success(`Специалист "${specialist.attributes.name}" добавлен успешно`);
            this.$refs.list.reload();
        },

        _onSpecialistUpdated({ specialist }) {
            this.$message.success(`Специалист "${specialist.attributes.name}" обновлен успешно`);
            this.$refs.list.reload();
        },

        _onSpecialistPasswordReset({ passwordResetResult }) {
            this.passwordResetResult = passwordResetResult;
            this.passwordResetDialogVisible = true;
        },

        _exportPasswords() {
            this.$confirm(
                'Сгенерировать новые пароли для всех специалистов и скачать xlsx?',
                'Подтверждение',
                {
                    type: 'warning'
                }
            )
                .then(() => {
                    this.exportPasswordsLoading = true;

                    return Specialist.exportPasswords()
                        .then(({ blob, fileName }) => {
                            const url = window.URL.createObjectURL(blob);
                            const link = document.createElement('a');
                            link.href = url;
                            link.download = fileName || 'specialists-passwords.xlsx';
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                            window.URL.revokeObjectURL(url);

                            this.$message.success('Новые пароли сгенерированы');
                        })
                        .catch(error => {
                            const message = _.get(error, 'message') || 'Не удалось сгенерировать пароли';
                            this.$message.error(message);
                        })
                        .finally(() => {
                            this.exportPasswordsLoading = false;
                        });
                })
                .catch(() => {
                });
        },

        _copyPasswordCard() {
            const text = this.passwordResetCard;
            if (!text) {
                return;
            }

            if (navigator.clipboard && navigator.clipboard.writeText) {
                navigator.clipboard.writeText(text)
                    .then(() => {
                        this.$message.success('Карточка скопирована');
                    })
                    .catch(() => {
                        this._copyPasswordCardFallback(text);
                    });
                return;
            }

            this._copyPasswordCardFallback(text);
        },

        _copyPasswordCardFallback(text) {
            const textarea = document.createElement('textarea');
            textarea.value = text;
            textarea.setAttribute('readonly', '');
            textarea.style.position = 'absolute';
            textarea.style.left = '-9999px';
            document.body.appendChild(textarea);
            textarea.select();
            document.execCommand('copy');
            document.body.removeChild(textarea);
            this.$message.success('Карточка скопирована');
        }
    }
};
</script>

<style lang="scss">
@import "@vars";

.specialist-list-view {
    &__toolbar {
        display: flex;
        align-items: center;
        gap: 16px;
        margin-bottom: 16px;
    }

    &__search {
        max-width: 300px;
        width: 100%;
    }

    &__password-card-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 16px;
        margin-bottom: 12px;
        font-weight: 600;
    }

    &__password-card-footer {
        display: flex;
        justify-content: flex-end;
        margin-top: 16px;
    }
}
</style>
